(() => {
  if (customElements.get('ownart-product-compare-page')) return;

  class OwnArtProductComparePage extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleExternalUpdate = this.handleExternalUpdate.bind(this);
      this.handleStorage = this.handleStorage.bind(this);
    }

    connectedCallback() {
      this.storageKey = this.dataset.storageKey || 'ownart-product-compare';
      this.limit = Math.max(2, Number.parseInt(this.dataset.compareLimit || '3', 10) || 3);
      this.empty = this.querySelector('[data-compare-page-empty]');
      this.emptyCopy = this.querySelector('[data-compare-page-empty-copy]');
      this.content = this.querySelector('[data-compare-page-content]');
      this.matrix = this.querySelector('[data-compare-page-matrix]');
      this.status = this.querySelector('[data-compare-page-status]');
      this.clearButton = this.querySelector('[data-compare-page-clear]');
      this.returnLinks = Array.from(this.querySelectorAll('[data-compare-page-browse], [data-compare-page-back]'));
      this.slots = this.querySelector('[data-compare-page-slots]');
      this.selectionCopy = this.querySelector('[data-compare-page-selection-copy]');
      this.pickerDialog = this.querySelector('[data-compare-page-picker-dialog]');
      this.addEventListener('click', this.handleClick);
      window.addEventListener('ownart:product-compare-updated', this.handleExternalUpdate);
      window.addEventListener('storage', this.handleStorage);
      window.addEventListener('pageshow', this.handleExternalUpdate);
      this.syncReturnLinks();
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('ownart:product-compare-updated', this.handleExternalUpdate);
      window.removeEventListener('storage', this.handleStorage);
      window.removeEventListener('pageshow', this.handleExternalUpdate);
    }

    get labels() {
      return {
        remove: this.dataset.labelRemove || 'Remove [product]',
        price: this.dataset.labelPrice || 'Price',
        availability: this.dataset.labelAvailability || 'Availability',
        available: this.dataset.labelAvailable || 'Available',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        product: this.dataset.labelProduct || 'Product',
        style: this.dataset.labelStyle || 'Style / category',
        material: this.dataset.labelMaterial || 'Material',
        stone: this.dataset.labelStone || 'Stone',
        setting: this.dataset.labelSetting || 'Setting',
        sizeLength: this.dataset.labelSizeLength || 'Size / length',
        weight: this.dataset.labelWeight || 'Weight',
        dimensions: this.dataset.labelDimensions || 'Dimensions',
        viewProduct: this.dataset.labelViewProduct || 'View product',
        notAvailable: this.dataset.labelNotAvailable || 'Not available',
        empty: this.dataset.labelEmpty || 'Choose products from the picker to begin.',
        one: this.dataset.labelOne || 'One product selected. Choose one more product to start the comparison.',
        clear: this.dataset.labelClear || 'Clear selection',
        selectProduct: this.dataset.labelSelectProduct || 'Choose product',
        changeProduct: this.dataset.labelChangeProduct || 'Change product',
        addThird: this.dataset.labelAddThird || 'Add a third piece',
        slot: this.dataset.labelSlot || 'Piece [count]',
        zero: this.dataset.labelZero || 'Choose two pieces for a clear side-by-side comparison.',
        ready: this.dataset.labelReady || 'Two pieces selected. You can compare now or add a third piece.',
        readyThree: this.dataset.labelReadyThree || 'Three pieces selected.'
      };
    }

    normalizeProduct(value) {
      if (!value || value.id === undefined || value.id === null) return null;

      const options = Array.isArray(value.options)
        ? value.options
            .map((option) => ({
              name: String(option?.name || '').trim(),
              values: Array.isArray(option?.values)
                ? option.values.map((item) => String(item || '').trim()).filter(Boolean)
                : String(option?.value || '').split(',').map((item) => item.trim()).filter(Boolean)
            }))
            .filter((option) => option.name)
        : [];
      const details = Array.isArray(value.details) ? value.details.map((item) => String(item || '').trim()).filter(Boolean) : [];
      const firstDetail = (...needles) => details.find((detail) => {
        const normalized = detail.toLowerCase();
        return needles.some((needle) => normalized.includes(needle));
      }) || '';
      const readString = (...values) => {
        const valueToUse = values.find((item) => item !== undefined && item !== null && String(item).trim() && String(item).trim() !== '0');
        return valueToUse === undefined ? '' : String(valueToUse).trim();
      };

      return {
        id: String(value.id),
        title: String(value.title || '').trim(),
        url: String(value.url || '').trim(),
        image: String(value.image || '').trim(),
        imageAlt: String(value.imageAlt || value.image_alt || value.title || '').trim(),
        priceFormatted: String(value.priceFormatted || value.price_formatted || value.price || '').trim(),
        compareAtPriceFormatted: String(value.compareAtPriceFormatted || value.compare_at_price_formatted || '').trim(),
        available: value.available === true || value.available === 'true',
        type: String(value.type || '').trim(),
        style: readString(value.style, value.type),
        vendor: String(value.vendor || '').trim(),
        weight: readString(value.weight, value.weight_grams),
        material: readString(value.material, firstDetail('gold', 'silver', 'plated', 'vermeil', 'steel')),
        stone: readString(value.stone, firstDetail('synthetic', 'cz', 'zirconia', 'pearl', 'diamond', 'gemstone')),
        setting: readString(value.setting, firstDetail('setting')),
        dimensions: readString(value.dimensions),
        intro: readString(value.intro),
        options,
        details
      };
    }

    getSelection() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(stored)) return [];
        return stored.map((item) => this.normalizeProduct(item)).filter(Boolean).slice(0, this.limit);
      } catch (error) {
        return [];
      }
    }

    saveSelection(selection) {
      const normalized = selection.map((item) => this.normalizeProduct(item)).filter(Boolean).slice(0, this.limit);
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(normalized));
      } catch (error) {}
      window.dispatchEvent(new CustomEvent('ownart:product-compare-updated'));
      this.render();
    }

    resolveReturnUrl() {
      const returnTo = new URLSearchParams(window.location.search).get('return_to');
      if (!returnTo) return '';

      try {
        const returnUrl = new URL(returnTo, window.location.origin);
        if (returnUrl.origin !== window.location.origin || !returnUrl.pathname.startsWith('/')) return '';
        return `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
      } catch (error) {
        return '';
      }
    }

    syncReturnLinks() {
      const returnUrl = this.resolveReturnUrl();
      if (!returnUrl) return;

      this.returnLinks.forEach((link) => {
        link.href = returnUrl;
      });
    }

    readProduct(button) {
      const card = button.closest('[data-product-compare-card]');
      const source = card?.querySelector('script[data-product-compare-data]');
      if (!source?.textContent) return null;
      try {
        return this.normalizeProduct(JSON.parse(source.textContent));
      } catch (error) {
        return null;
      }
    }

    handleClick(event) {
      const replacementToggle = event.target.closest('[data-product-compare-toggle]');
      if (replacementToggle && this.replacingId && this.pickerDialog?.contains(replacementToggle)) {
        const replacement = this.readProduct(replacementToggle);
        const selection = this.getSelection();
        const replacedIndex = selection.findIndex((product) => product.id === this.replacingId);
        const selectedIndex = selection.findIndex((product) => product.id === replacement?.id);
        if (replacement && replacedIndex >= 0) {
          if (selectedIndex >= 0) {
            [selection[replacedIndex], selection[selectedIndex]] = [selection[selectedIndex], selection[replacedIndex]];
          } else {
            selection[replacedIndex] = replacement;
          }
          this.replacingId = '';
          this.saveSelection(selection);
          this.closePicker();
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const removeButton = event.target.closest('[data-compare-page-remove]');
      if (removeButton && this.contains(removeButton)) {
        this.saveSelection(this.getSelection().filter((product) => product.id !== removeButton.dataset.comparePageRemove));
        return;
      }

      const clearButton = event.target.closest('[data-compare-page-clear]');
      if (clearButton && this.contains(clearButton)) {
        this.saveSelection([]);
        return;
      }

      const replaceButton = event.target.closest('[data-compare-page-replace]');
      if (replaceButton && this.contains(replaceButton)) {
        this.openPicker(replaceButton.dataset.comparePageReplace);
        return;
      }

      const pickerOpen = event.target.closest('[data-compare-page-picker-open], [data-compare-page-slot]');
      if (pickerOpen && this.contains(pickerOpen)) {
        this.openPicker();
        return;
      }

      const pickerClose = event.target.closest('[data-compare-page-picker-close], [data-compare-page-picker-done]');
      if (pickerClose && this.contains(pickerClose)) {
        this.closePicker();
        return;
      }

      if (event.target === this.pickerDialog) this.closePicker();
    }

    handleExternalUpdate() {
      this.render();
    }

    handleStorage(event) {
      if (!event.key || event.key === this.storageKey) this.render();
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    openPicker(replacingId = '') {
      this.replacingId = String(replacingId || '');
      if (!this.pickerDialog) return;
      if (typeof this.pickerDialog.showModal === 'function') {
        if (!this.pickerDialog.open) this.pickerDialog.showModal();
      } else {
        this.pickerDialog.setAttribute('open', '');
      }
    }

    closePicker() {
      this.replacingId = '';
      if (!this.pickerDialog) return;
      if (typeof this.pickerDialog.close === 'function' && this.pickerDialog.open) {
        this.pickerDialog.close();
      } else {
        this.pickerDialog.removeAttribute('open');
      }
    }

    render() {
      const selection = this.getSelection();
      const ready = selection.length >= 2;
      if (this.empty) this.empty.hidden = ready;
      if (this.content) this.content.hidden = !ready;
      if (this.clearButton) this.clearButton.hidden = selection.length === 0;
      if (this.selectionCopy) {
        this.selectionCopy.textContent = selection.length === 0
          ? this.labels.zero
          : selection.length === 1
            ? this.labels.one
            : selection.length === 2
              ? this.labels.ready
              : this.labels.readyThree;
      }
      this.renderSlots(selection);

      if (!ready) {
        if (this.emptyCopy) this.emptyCopy.textContent = selection.length === 1 ? this.labels.one : this.labels.empty;
        this.setStatus(selection.length === 1 ? this.labels.one : this.labels.empty);
        if (this.matrix) this.matrix.replaceChildren();
        return;
      }

      this.setStatus('');
      this.renderMatrix(selection);
    }

    renderSlots(selection) {
      if (!this.slots) return;
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < this.limit; index += 1) {
        const product = selection[index];
        if (!product) {
          const slot = document.createElement('button');
          slot.type = 'button';
          slot.className = 'ownart-product-compare-page__slot ownart-product-compare-page__slot--empty';
          slot.dataset.comparePageSlot = '';
          slot.dataset.comparePageSlotIndex = String(index);
          const label = document.createElement('span');
          label.textContent = this.labels.slot.replace('[count]', String(index + 1));
          const action = document.createElement('strong');
          action.textContent = index === 2 && selection.length >= 2 ? this.labels.addThird : this.labels.selectProduct;
          slot.append(label, action);
          fragment.append(slot);
          continue;
        }

        const slot = document.createElement('article');
        slot.className = 'ownart-product-compare-page__slot';
        if (product.image) {
          const image = document.createElement('img');
          image.className = 'ownart-product-compare-page__slot-image';
          image.src = product.image;
          image.alt = product.imageAlt;
          image.loading = 'lazy';
          slot.append(image);
        }
        const copy = document.createElement('div');
        copy.className = 'ownart-product-compare-page__slot-copy';
        const label = document.createElement('span');
        label.textContent = this.labels.slot.replace('[count]', String(index + 1));
        const title = document.createElement('strong');
        title.textContent = product.title;
        copy.append(label, title);
        slot.append(copy);

        const actions = document.createElement('div');
        actions.className = 'ownart-product-compare-page__slot-actions';
        const replace = document.createElement('button');
        replace.type = 'button';
        replace.dataset.comparePageReplace = product.id;
        replace.textContent = this.labels.changeProduct;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.comparePageRemove = product.id;
        remove.textContent = this.labels.remove.replace('[product]', product.title);
        actions.append(replace, remove);
        slot.append(actions);
        fragment.append(slot);
      }
      this.slots.replaceChildren(fragment);
    }

    renderMatrix(selection) {
      if (!this.matrix) return;
      this.matrix.style.setProperty('--ownart-compare-page-columns', String(selection.length));
      this.matrix.dataset.compareColumns = String(selection.length);
      const matrix = document.createDocumentFragment();
      this.appendProductHeader(matrix, selection);
      this.appendRow(matrix, this.labels.price, selection.map((product) => {
        if (!product.compareAtPriceFormatted) return product.priceFormatted || this.labels.notAvailable;
        return `${product.priceFormatted} (${product.compareAtPriceFormatted})`;
      }));
      this.appendRow(matrix, this.labels.availability, selection.map((product) => product.available ? this.labels.available : this.labels.soldOut));
      this.appendRow(matrix, this.labels.style, selection.map((product) => product.style || product.type || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.material, selection.map((product) => product.material || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.stone, selection.map((product) => product.stone || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.setting, selection.map((product) => product.setting || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.sizeLength, selection.map((product) => this.formatSizingOptions(product) || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.weight, selection.map((product) => product.weight || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.dimensions, selection.map((product) => product.dimensions || this.labels.notAvailable));

      const otherOptionNames = [];
      selection.forEach((product) => product.options.forEach((option) => {
        if (!this.isSizingOption(option.name) && !otherOptionNames.includes(option.name)) otherOptionNames.push(option.name);
      }));
      otherOptionNames.forEach((name) => {
        this.appendRow(matrix, name, selection.map((product) => product.options.find((option) => option.name === name)?.values.join(', ') || this.labels.notAvailable));
      });
      this.matrix.replaceChildren(matrix);
    }

    isSizingOption(name) {
      const normalized = String(name || '').toLowerCase();
      return normalized.includes('size') || normalized.includes('length') || normalized.includes('dimension') || normalized.includes('尺寸') || normalized.includes('长度');
    }

    formatSizingOptions(product) {
      const options = product.options.filter((option) => this.isSizingOption(option.name));
      return options.map((option) => `${option.name}: ${option.values.join(', ')}`).join('; ');
    }

    appendProductHeader(fragment, selection) {
      fragment.append(this.createCell(this.labels.product, 'label'));
      selection.forEach((product) => {
        const cell = document.createElement('div');
        cell.className = 'ownart-product-compare-page__cell ownart-product-compare-page__product';
        cell.setAttribute('role', 'cell');
        if (product.image) {
          const image = document.createElement('img');
          image.className = 'ownart-product-compare-page__product-image';
          image.src = product.image;
          image.alt = product.imageAlt;
          image.loading = 'lazy';
          cell.append(image);
        }
        const title = document.createElement('h2');
        title.className = 'ownart-product-compare-page__product-title';
        title.textContent = product.title;
        cell.append(title);

        const actions = document.createElement('div');
        actions.className = 'ownart-product-compare-page__product-actions';
        if (product.url) {
          const link = document.createElement('a');
          link.className = 'ownart-product-compare-page__product-link';
          link.href = product.url;
          link.textContent = this.labels.viewProduct;
          actions.append(link);
        }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ownart-product-compare-page__remove';
        remove.dataset.comparePageRemove = product.id;
        remove.textContent = this.labels.remove.replace('[product]', product.title);
        remove.setAttribute('aria-label', this.labels.remove.replace('[product]', product.title));
        actions.append(remove);
        cell.append(actions);
        fragment.append(cell);
      });
    }

    appendRow(fragment, label, values) {
      fragment.append(this.createCell(label, 'label'));
      values.forEach((value) => fragment.append(this.createCell(value)));
    }

    createCell(value, modifier = '') {
      const cell = document.createElement('div');
      cell.className = `ownart-product-compare-page__cell${modifier ? ` ownart-product-compare-page__${modifier}` : ''}`;
      cell.setAttribute('role', 'cell');
      cell.textContent = value;
      return cell;
    }
  }

  customElements.define('ownart-product-compare-page', OwnArtProductComparePage);
})();
